import React, { useState, useRef } from "react"
import { debounce } from "lodash"
import { useEditor, EditorContent } from "@tiptap/react"
import StarterKit from "@tiptap/starter-kit"
import { TextAlign } from "@tiptap/extension-text-align"
import { Highlight } from "@tiptap/extension-highlight"
import { Image } from "@tiptap/extension-image"
import { Youtube } from "@tiptap/extension-youtube"
import { TextStyle } from "@tiptap/extension-text-style"
import { Color } from "@tiptap/extension-color"
import { FontFamily } from "@tiptap/extension-font-family"
import { Table } from "@tiptap/extension-table"
import { TableRow } from "@tiptap/extension-table-row"
import { TableHeader } from "@tiptap/extension-table-header"
import { TableCell } from "@tiptap/extension-table-cell"
import {
    Box,
    Paper,
    IconButton,
    Divider,
    Toolbar,
    Tooltip,
    Select,
    MenuItem,
    FormControl,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    Button,
    Menu,
    MenuList,
    ListItemIcon,
    ListItemText,
} from "@mui/material"
import {
    FormatBold,
    FormatItalic,
    FormatUnderlined,
    FormatStrikethrough,
    FormatListBulleted,
    FormatListNumbered,
    FormatQuote,
    Undo,
    Redo,
    FormatAlignLeft,
    FormatAlignCenter,
    FormatAlignRight,
    FormatAlignJustify,
    Highlight as HighlightIcon,
    TableChart,
    HorizontalRule,
    Image as ImageIcon,
    Link as LinkIcon,
    YouTube,
    FormatColorText,
    Subscript,
    Superscript,
    FormatIndentDecrease,
    FormatIndentIncrease,
    Code,
    FormatClear,
    TableRows,
    TableView,
} from "@mui/icons-material"
import { FontSize } from "./FontSize";
import { SuperscriptMark } from "./SuperscriptMark";
import { SubscriptMark } from "./SubscriptMark";


const TiptapEditor = ({ content, 
    onChange, 
    placeholder, 
    editable = true, 
    minHeight = 100, 
    sx 
}) => {
    const [imageDialog, setImageDialog] = useState(false)
    const [linkDialog, setLinkDialog] = useState(false)
    const [youtubeDialog, setYoutubeDialog] = useState(false)
    const [imageUrl, setImageUrl] = useState("")
    const [linkUrl, setLinkUrl] = useState("")
    const [linkText, setLinkText] = useState("")
    const [youtubeUrl, setYoutubeUrl] = useState("")
    const [tableMenu, setTableMenu] = useState(null)
    const [colorMenu, setColorMenu] = useState(null)

    const lastContentRef = useRef(content)
    const debouncedChange = useRef(
        debounce((newHTML) => {
            if (newHTML !== lastContentRef.current) {
                lastContentRef.current = newHTML
                onChange?.(newHTML)
            }
        }, 500)
    ).current
    
    const editor = useEditor({
        extensions: [
            StarterKit.configure({
                link: {
                    openOnClick: false,
                    HTMLAttributes: {
                        class: "text-blue-600 underline hover:text-blue-800",
                    },
                },
            }),
            TextStyle,
            Color,
            FontSize,
            FontFamily,
            SuperscriptMark, 
            SubscriptMark,
            TextAlign.configure({
                types: ["heading", "paragraph"],
            }),
            Highlight.configure({
                multicolor: true,
            }),
            Image.configure({
                HTMLAttributes: {
                    class: "max-w-full h-auto rounded-lg",
                },
            }),            
            Youtube.configure({
                width: 640,
                height: 480,
                ccLanguage: "vi",
            }),
            Table.configure({
                resizable: true,
            }),
            TableRow,
            TableHeader,
            TableCell,
        ],
        content,
        editable,
        onUpdate: ({ editor }) => {
            if (!editable) return
            const newHTML = editor.getHTML()
            debouncedChange(newHTML)
        },
        editorProps: {
            attributes: {
                class: "prose prose-sm sm:prose lg:prose-lg xl:prose-2xl mx-auto focus:outline-none min-h-[200px] p-4",
            },
        },
    })

    if (!editor) {
        return null
    }

    const insertImage = () => {
        if (imageUrl) {
            editor.chain().focus().setImage({ src: imageUrl }).run()
            setImageUrl("")
            setImageDialog(false)
        }
    }

    const insertLink = () => {
        if (linkUrl) {
            if (linkText) {
                editor.chain().focus().insertContent(`<a href="${linkUrl}">${linkText}</a>`).run()
            } else {
                editor.chain().focus().setLink({ href: linkUrl }).run()
            }
            setLinkUrl("")
            setLinkText("")
            setLinkDialog(false)
        }
    }

    const insertYoutube = () => {
        if (youtubeUrl) {
            editor.chain().focus().setYoutubeVideo({ src: youtubeUrl }).run()
            setYoutubeUrl("")
            setYoutubeDialog(false)
        }
    }

    const colors = [
        "#000000",
        "#FF0000",
        "#00FF00",
        "#0000FF",
        "#FFFF00",
        "#FF00FF",
        "#00FFFF",
        "#FFA500",
        "#800080",
        "#008000",
    ]

    return (
        <Paper variant="outlined" sx={{ borderRadius: 1, width: '100%', ...sx }}>
            {editable && (
                <Toolbar variant="dense" sx={{ minHeight: 48, px: 1, borderBottom: 1, borderColor: "divider", flexWrap: "wrap" }}>
                    <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5, alignItems: "center" }}>
                        {/* Font Size */}
                        <FormControl size="small" sx={{ minWidth: 80 }}>
                            <Select
                                value=""
                                displayEmpty
                                onChange={(e) => {
                                    const size = e.target.value
                                    if (size) {
                                        editor.chain().focus().setFontSize(size).run();
                                    }
                                }}
                            >
                                <MenuItem value="">Size</MenuItem>
                                <MenuItem value="12">12px</MenuItem>
                                <MenuItem value="14">14px</MenuItem>
                                <MenuItem value="16">16px</MenuItem>
                                <MenuItem value="18">18px</MenuItem>
                                <MenuItem value="20">20px</MenuItem>
                                <MenuItem value="24">24px</MenuItem>
                            </Select>
                        </FormControl>

                        <Divider orientation="vertical" flexItem sx={{ mx: 0.5 }} />

                        {/* Text Formatting */}
                        <Tooltip title="Bold (Ctrl+B)">
                            <span>
                                <IconButton
                                    size="small"
                                    color={editor.isActive("bold") ? "primary" : "default"}
                                    onClick={() => editor.chain().focus().toggleBold().run()}
                                >
                                    <FormatBold fontSize="small" />
                                </IconButton>
                            </span>
                        </Tooltip>

                        <Tooltip title="Italic (Ctrl+I)">
                            <span>
                                <IconButton
                                    size="small"
                                    color={editor.isActive("italic") ? "primary" : "default"}
                                    onClick={() => editor.chain().focus().toggleItalic().run()}
                                >
                                    <FormatItalic fontSize="small" />
                                </IconButton>
                            </span>
                        </Tooltip>

                        <Tooltip title="Underline (Ctrl+U)">
                            <span>
                                <IconButton
                                    size="small"
                                    color={editor.isActive("underline") ? "primary" : "default"}
                                    onClick={() => editor.chain().focus().toggleUnderline().run()}
                                >
                                    <FormatUnderlined fontSize="small" />
                                </IconButton>
                            </span>
                        </Tooltip>

                        <Tooltip title="Strikethrough">
                            <span>
                                <IconButton
                                    size="small"
                                    color={editor.isActive("strike") ? "primary" : "default"}
                                    onClick={() => editor.chain().focus().toggleStrike().run()}
                                >
                                    <FormatStrikethrough fontSize="small" />
                                </IconButton>                            
                            </span>
                        </Tooltip>

                        <Tooltip title="Superscript">
                            <span>
                                <IconButton
                                    size="small"
                                    color={editor.isActive("superscript") ? "primary" : "default"}
                                    onClick={() => editor.chain().focus().toggleSuperscript().run()}
                                >
                                    <Superscript fontSize="small" />
                                </IconButton>                            
                            </span>
                        </Tooltip>

                        <Tooltip title="Subscript">
                            <span>
                                <IconButton
                                    size="small"
                                    color={editor.isActive("subscript") ? "primary" : "default"}
                                    onClick={() => editor.chain().focus().toggleSubscript().run()}
                                >
                                    <Subscript fontSize="small" />
                                </IconButton>                            
                            </span>
                        </Tooltip>

                        <Tooltip title="Highlight">
                            <span>
                                <IconButton
                                    size="small"
                                    color={editor.isActive("highlight") ? "primary" : "default"}
                                    onClick={() => editor.chain().focus().toggleHighlight().run()}
                                >
                                    <HighlightIcon fontSize="small" />
                                </IconButton>                            
                            </span>
                        </Tooltip>

                        <Tooltip title="Text color">
                            <span>
                                <IconButton size="small" onClick={(e) => setColorMenu(e.currentTarget)}>
                                    <FormatColorText fontSize="small" />
                                </IconButton>                            
                            </span>
                        </Tooltip>

                        <Tooltip title="Code">
                            <span>
                                <IconButton
                                    size="small"
                                    color={editor.isActive("code") ? "primary" : "default"}
                                    onClick={() => editor.chain().focus().toggleCode().run()}
                                >
                                    <Code fontSize="small" />
                                </IconButton>                            
                            </span>
                        </Tooltip>

                        <Tooltip title="Clear formatting">
                            <span>
                                <IconButton size="small" onClick={() => editor.chain().focus().clearNodes().unsetAllMarks().run()}>
                                    <FormatClear fontSize="small" />
                                </IconButton>                            
                            </span>
                        </Tooltip>

                        <Divider orientation="vertical" flexItem sx={{ mx: 0.5 }} />

                        {/* Lists */}
                        <Tooltip title="Bullet list">
                            <span>
                                <IconButton
                                    size="small"
                                    color={editor.isActive("bulletList") ? "primary" : "default"}
                                    onClick={() => editor.chain().focus().toggleBulletList().run()}
                                >
                                    <FormatListBulleted fontSize="small" />
                                </IconButton>                            
                            </span>
                        </Tooltip>

                        <Tooltip title="Numbered list">
                            <span>
                                <IconButton
                                    size="small"
                                    color={editor.isActive("orderedList") ? "primary" : "default"}
                                    onClick={() => editor.chain().focus().toggleOrderedList().run()}
                                >
                                    <FormatListNumbered fontSize="small" />
                                </IconButton>                            
                            </span>
                        </Tooltip>

                        <Tooltip title="Blockquote">
                            <span>
                                <IconButton
                                    size="small"
                                    color={editor.isActive("blockquote") ? "primary" : "default"}
                                    onClick={() => editor.chain().focus().toggleBlockquote().run()}
                                >
                                    <FormatQuote fontSize="small" />
                                </IconButton>                            
                            </span>
                        </Tooltip>

                        <Tooltip title="Decrease indent">
                            <span>
                                <IconButton size="small" onClick={() => editor.chain().focus().liftListItem("listItem").run()}>
                                    <FormatIndentDecrease fontSize="small" />
                                </IconButton>                            
                            </span>
                        </Tooltip>

                        <Tooltip title="Increase indent">
                            <span>
                                <IconButton size="small" onClick={() => editor.chain().focus().sinkListItem("listItem").run()}>
                                    <FormatIndentIncrease fontSize="small" />
                                </IconButton>                            
                            </span>
                        </Tooltip>

                        <Divider orientation="vertical" flexItem sx={{ mx: 0.5 }} />

                        {/* Alignment */}
                        <Tooltip title="Align left">
                            <span>
                                <IconButton
                                    size="small"
                                    color={editor.isActive({ textAlign: "left" }) ? "primary" : "default"}
                                    onClick={() => editor.chain().focus().setTextAlign("left").run()}
                                >
                                    <FormatAlignLeft fontSize="small" />
                                </IconButton>                            
                            </span>
                        </Tooltip>

                        <Tooltip title="Align center">
                            <span>
                                <IconButton
                                    size="small"
                                    color={editor.isActive({ textAlign: "center" }) ? "primary" : "default"}
                                    onClick={() => editor.chain().focus().setTextAlign("center").run()}
                                >
                                    <FormatAlignCenter fontSize="small" />
                                </IconButton>                            
                            </span>
                        </Tooltip>

                        <Tooltip title="Align right">
                            <span>
                                <IconButton
                                    size="small"
                                    color={editor.isActive({ textAlign: "right" }) ? "primary" : "default"}
                                    onClick={() => editor.chain().focus().setTextAlign("right").run()}
                                >
                                    <FormatAlignRight fontSize="small" />
                                </IconButton>                            
                            </span>
                        </Tooltip>

                        <Tooltip title="Justify">
                            <span>
                                <IconButton
                                    size="small"
                                    color={editor.isActive({ textAlign: "justify" }) ? "primary" : "default"}
                                    onClick={() => editor.chain().focus().setTextAlign("justify").run()}
                                >
                                    <FormatAlignJustify fontSize="small" />
                                </IconButton>                            
                            </span>
                        </Tooltip>

                        <Divider orientation="vertical" flexItem sx={{ mx: 0.5 }} />

                        {/* Media */}
                        <Tooltip title="Insert image">
                            <span>
                                <IconButton size="small" onClick={() => setImageDialog(true)}>
                                    <ImageIcon fontSize="small" />
                                </IconButton>                            
                            </span>
                        </Tooltip>

                        <Tooltip title="Insert link">
                            <span>
                                <IconButton size="small" onClick={() => setLinkDialog(true)}>
                                    <LinkIcon fontSize="small" />
                                </IconButton>                            
                            </span>
                        </Tooltip>

                        <Tooltip title="Insert YouTube video">
                            <span>
                                <IconButton size="small" onClick={() => setYoutubeDialog(true)}>
                                    <YouTube fontSize="small" />
                                </IconButton>                            
                            </span>
                        </Tooltip>

                        <Divider orientation="vertical" flexItem sx={{ mx: 0.5 }} />

                        {/* Table */}
                        <Tooltip title="Table">
                            <span>
                                <IconButton size="small" onClick={(e) => setTableMenu(e.currentTarget)}>
                                    <TableChart fontSize="small" />
                                </IconButton>                            
                            </span>
                        </Tooltip>

                        <Divider orientation="vertical" flexItem sx={{ mx: 0.5 }} />

                        {/* Divider */}
                        <Tooltip title="Horizontal rule">
                            <span>
                                <IconButton size="small" onClick={() => editor.chain().focus().setHorizontalRule().run()}>
                                    <HorizontalRule fontSize="small" />
                                </IconButton>                            
                            </span>
                        </Tooltip>

                        <Divider orientation="vertical" flexItem sx={{ mx: 0.5 }} />

                        {/* Undo/Redo */}
                        <Tooltip title="Undo (Ctrl+Z)">
                            <span>
                                <IconButton
                                    size="small"
                                    onClick={() => editor.chain().focus().undo().run()}
                                    disabled={!editor.can().undo()}
                                >
                                    <Undo fontSize="small" />
                                </IconButton>                            
                            </span>
                        </Tooltip>

                        <Tooltip title="Redo (Ctrl+Y)">
                            <span>
                                <IconButton
                                    size="small"
                                    onClick={() => editor.chain().focus().redo().run()}
                                    disabled={!editor.can().redo()}
                                >
                                    <Redo fontSize="small" />
                                </IconButton>                            
                            </span>
                        </Tooltip>
                    </Box>
                </Toolbar>
            )}

            <Box sx={{ minHeight, "& .ProseMirror": { outline: "none", p: 2 } }}>
                <EditorContent editor={editor} placeholder={placeholder} />
            </Box>

            {/* Color Menu */}
            <Menu anchorEl={colorMenu} open={Boolean(colorMenu)} onClose={() => setColorMenu(null)}>
                <Box sx={{ p: 1, display: "grid", gridTemplateColumns: "repeat(5, 1fr)", gap: 1 }}>
                    {colors.map((color) => (
                        <IconButton
                            key={color}
                            size="small"
                            sx={{ bgcolor: color, width: 24, height: 24, "&:hover": { opacity: 0.8 } }}
                            onClick={() => {
                                editor.chain().focus().setColor(color).run()
                                setColorMenu(null)
                            }}
                        />
                    ))}
                </Box>
            </Menu>

            {/* Table Menu */}
            <Menu anchorEl={tableMenu} open={Boolean(tableMenu)} onClose={() => setTableMenu(null)}>
                <MenuList>
                    <MenuItem
                        onClick={() => {
                            editor.chain().focus().insertTable({ rows: 3, cols: 3, withHeaderRow: true }).run()
                            setTableMenu(null)
                        }}
                    >
                        <ListItemIcon>
                            <TableView />
                        </ListItemIcon>
                        <ListItemText>Insert 3×3 table</ListItemText>
                    </MenuItem>
                    <MenuItem
                        onClick={() => {
                            editor.chain().focus().addRowBefore().run()
                            setTableMenu(null)
                        }}
                    >
                        <ListItemIcon>
                            <TableRows />
                        </ListItemIcon>
                        <ListItemText>Add row before</ListItemText>
                    </MenuItem>
                    <MenuItem
                        onClick={() => {
                            editor.chain().focus().addRowAfter().run()
                            setTableMenu(null)
                        }}
                    >
                        <ListItemIcon>
                            <TableRows />
                        </ListItemIcon>
                        <ListItemText>Add row after</ListItemText>
                    </MenuItem>
                    <MenuItem
                        onClick={() => {
                            editor.chain().focus().deleteRow().run()
                            setTableMenu(null)
                        }}
                    >
                        <ListItemText>Delete row</ListItemText>
                    </MenuItem>
                    <MenuItem
                        onClick={() => {
                            editor.chain().focus().addColumnBefore().run()
                            setTableMenu(null)
                        }}
                    >
                        <ListItemText>Add column before</ListItemText>
                    </MenuItem>
                    <MenuItem
                        onClick={() => {
                            editor.chain().focus().addColumnAfter().run()
                            setTableMenu(null)
                        }}
                    >
                        <ListItemText>Add column after</ListItemText>
                    </MenuItem>
                    <MenuItem
                        onClick={() => {
                            editor.chain().focus().deleteColumn().run()
                            setTableMenu(null)
                        }}
                    >
                        <ListItemText>Delete column</ListItemText>
                    </MenuItem>
                    <MenuItem
                        onClick={() => {
                            editor.chain().focus().deleteTable().run()
                            setTableMenu(null)
                        }}
                    >
                        <ListItemText>Delete table</ListItemText>
                    </MenuItem>
                </MenuList>
            </Menu>

            {/* Image Dialog */}
            <Dialog open={imageDialog} onClose={() => setImageDialog(false)} maxWidth="sm" fullWidth>
                <DialogTitle>Insert image</DialogTitle>
                <DialogContent>
                    <TextField
                        autoFocus
                        margin="dense"
                        label="Image URL"
                        type="url"
                        fullWidth
                        variant="outlined"
                        value={imageUrl}
                        onChange={(e) => setImageUrl(e.target.value)}
                        placeholder="https://example.com/image.jpg"
                    />
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setImageDialog(false)}>Cancel</Button>
                    <Button onClick={insertImage} variant="contained">Insert
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Link Dialog */}
            <Dialog open={linkDialog} onClose={() => setLinkDialog(false)} maxWidth="sm" fullWidth>
                <DialogTitle>Insert link</DialogTitle>
                <DialogContent>
                    <TextField
                        autoFocus
                        margin="dense"
                        label="Link URL"
                        type="url"
                        fullWidth
                        variant="outlined"
                        value={linkUrl}
                        onChange={(e) => setLinkUrl(e.target.value)}
                        placeholder="https://example.com"
                        sx={{ mb: 2 }}
                    />
                    <TextField
                        margin="dense"
                        label="Display text (optional)"
                        type="text"
                        fullWidth
                        variant="outlined"
                        value={linkText}
                        onChange={(e) => setLinkText(e.target.value)}
                        placeholder="Enter display text"
                    />
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setLinkDialog(false)}>Cancel</Button>
                    <Button onClick={insertLink} variant="contained">Insert
                    </Button>
                </DialogActions>
            </Dialog>

            {/* YouTube Dialog */}
            <Dialog open={youtubeDialog} onClose={() => setYoutubeDialog(false)} maxWidth="sm" fullWidth>
                <DialogTitle>Insert YouTube video</DialogTitle>
                <DialogContent>
                    <TextField
                        autoFocus
                        margin="dense"
                        label="URL YouTube"
                        type="url"
                        fullWidth
                        variant="outlined"
                        value={youtubeUrl}
                        onChange={(e) => setYoutubeUrl(e.target.value)}
                        placeholder="https://www.youtube.com/watch?v=..."
                    />
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setYoutubeDialog(false)}>Cancel</Button>
                    <Button onClick={insertYoutube} variant="contained">Insert
                    </Button>
                </DialogActions>
            </Dialog>
        </Paper>
    )
}

export default TiptapEditor;